import React, { useState } from 'react';
import './Quote.css';

const GOOGLE_SCRIPT_ENDPOINT =
  'https://script.google.com/macros/s/AKfycbwnlilxzfAvrt9q55F3cRU-DqeTn0ooVfwZv_N_OVA9VTFXlphQzEtrwtD-HXTydw7cOg/exec';

const Quote = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    carReg: '',
    vin: '',
    partNumber: '',
  });

  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.carReg.trim() && !formData.vin.trim()) {
      setErrorMessage('Please enter either a car registration or VIN number.');
      return;
    }

    setSubmitting(true);
    setErrorMessage('');

    try {
      const response = await fetch(GOOGLE_SCRIPT_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'text/plain;charset=utf-8',
        },
        body: JSON.stringify({
          formType: 'quote',
          ...formData,
        }),
      });

      if (!response.ok) {
        throw new Error('Unable to submit quote request. Please try again.');
      }

      setSubmitted(true);
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        carReg: '',
        vin: '',
        partNumber: '',
      });
      setTimeout(() => {
        setSubmitted(false);
      }, 3000);
    } catch (error) {
      setErrorMessage(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="quote-page">
      {/* Header */}
      <section className="quote-header">
        <div className="container">
          <h1>Get a Custom Quote</h1>
          <p>Tell us about your vehicle and we'll provide a personalized quote</p>
        </div>
      </section>

      <div className="container">
        <div className="quote-content">
          {/* Quote Form */}
          <section className="quote-form-section p-40">
            <h2>Quote Request Form</h2>
            <p className="form-intro">
              Fill out this form with details about your vehicle and the service you need. We'll
              contact you shortly with a custom quote.
            </p>

            {submitted && (
              <div className="success-message">
                ✓ Thank you! We've received your quote request. Our team will contact you within
                24 hours with a personalized quote.
              </div>
            )}

            {errorMessage && <div className="error-message">{errorMessage}</div>}

            <form onSubmit={handleSubmit} className="quote-form">
              {/* Personal Information */}
              <fieldset>
                <legend>Personal Information</legend>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="firstName">First Name *</label>
                    <input
                      type="text"
                      id="firstName"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      required
                      placeholder="John"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="lastName">Last Name *</label>
                    <input
                      type="text"
                      id="lastName"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      required
                      placeholder="Smith"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="email">Email Address *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="your@email.com"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="phone">Phone Number *</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      placeholder="02045793699"
                    />
                  </div>
                </div>
              </fieldset>

              {/* Vehicle Information */}
              <fieldset>
                <legend>Vehicle Information</legend>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="carReg">Car Registration</label>
                    <input
                      type="text"
                      id="carReg"
                      name="carReg"
                      value={formData.carReg}
                      onChange={handleChange}
                      placeholder="e.g., AB21 XYZ"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="vin">VIN Number</label>
                    <input
                      type="text"
                      id="vin"
                      name="vin"
                      value={formData.vin}
                      onChange={handleChange}
                      placeholder="Enter 17-character VIN"
                    />
                  </div>
                </div>

                <p className="form-hint">Please provide either a car registration or VIN number.</p>

                <div className="form-group">
                  <label htmlFor="partNumber">Part Number Requested</label>
                  <input
                    type="text"
                    id="partNumber"
                    name="partNumber"
                    value={formData.partNumber}
                    onChange={handleChange}
                    placeholder="Enter the requested part number, if known"
                  />
                </div>
              </fieldset>

              <button type="submit" className="btn btn-primary btn-large" disabled={submitting}>
                {submitting ? 'Sending...' : 'Request Quote'}
              </button>
            </form>
          </section>

          {/* Info Section */}
          <section className="quote-info p-40">
            <h2>Why Request a Quote?</h2>
            <div className="grid grid-1">
              <div className="info-item">
                <span className="item-icon">✓</span>
                <div>
                  <h3>Free Assessment</h3>
                  <p>No obligation - we provide accurate quotes based on your specific needs</p>
                </div>
              </div>
              <div className="info-item">
                <span className="item-icon">✓</span>
                <div>
                  <h3>Custom Pricing</h3>
                  <p>Every vehicle is unique. Get personalized pricing for your situation</p>
                </div>
              </div>
              <div className="info-item">
                <span className="item-icon">✓</span>
                <div>
                  <h3>Fast Response</h3>
                  <p>We respond within 24 hours with detailed quotes and service options</p>
                </div>
              </div>
              <div className="info-item">
                <span className="item-icon">✓</span>
                <div>
                  <h3>Insurance Support</h3>
                  <p>If you have insurance, we can handle direct billing to your provider</p>
                </div>
              </div>
              <div className="info-item">
                <span className="item-icon">✓</span>
                <div>
                  <h3>Payment Options</h3>
                  <p>Multiple payment methods available - card, bank transfer, or insurance billing</p>
                </div>
              </div>
              <div className="info-item">
                <span className="item-icon">✓</span>
                <div>
                  <h3>Quality Guaranteed</h3>
                  <p>All work backed by our quality guarantee and professional warranty</p>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>

      {/* Service Range Section */}
      <section className="service-range">
        <div className="container">
          <h2 className="text-center">Our Service Range & Pricing</h2>
          <div className="range-grid">
            <div className="range-card">
              <h3>Accident Repairs</h3>
              <p className="range-price">£200 - £3000</p>
              <p>Complete damage assessment and repair</p>
            </div>
            <div className="range-card">
              <h3>Dent Removal</h3>
              <p className="range-price">£80 - £500</p>
              <p>Paintless and traditional dent removal</p>
            </div>
            <div className="range-card">
              <h3>Scratch Repairs</h3>
              <p className="range-price">£50 - £300</p>
              <p>Surface and deep scratch treatment</p>
            </div>
            <div className="range-card">
              <h3>Full Body Respray</h3>
              <p className="range-price">£1000 - £4000</p>
              <p>Complete vehicle repainting</p>
            </div>
            <div className="range-card">
              <h3>Alloy Wheel Repair</h3>
              <p className="range-price">£60 - £150/wheel</p>
              <p>Wheel damage repair and refinishing</p>
            </div>
            <div className="range-card">
              <h3>Paint Correction</h3>
              <p className="range-price">£150 - £600</p>
              <p>Polishing and paint restoration</p>
            </div>
          </div>
        </div>
      </section>

     
    </main>
  );
};

export default Quote;
